import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Shield, Zap, FileText, Clock, TrendingUp, Users, Briefcase,
  Home, Heart, Gift, RefreshCw, ArrowUpCircle, Lightbulb, Star, Phone, Mail,
  MapPin, AlertTriangle, Headphones, Calculator, Building, Landmark
} from 'lucide-react';

const HomeLoan = () => {
  const [principal, setPrincipal] = useState(5000000);
  const [interest, setInterest] = useState(8.85);
  const [tenure, setTenure] = useState(20);

  const calculateEMI = () => {
    const p = principal;
    const r = interest / 12 / 100;
    const n = tenure * 12;
    const emi = p * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    return isNaN(emi) ? 0 : Math.round(emi);
  };

  const emi = calculateEMI();
  const totalPayable = emi * tenure * 12;
  const totalInterest = totalPayable - principal;

  return (
    <div className="bg-gray-50">
      <div className="mb-6">
        <div className="flex justify-center lg:justify-end">
          <img
            src="Home.png"
            alt="Home Loan"
            className="max-w-full h-full"
          />
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-16 max-w-4xl mx-auto text-center animate-fade-up">
          <h2 className="text-3xl font-bold text-green-700 mb-6">About Home Loan</h2>
          <p className="text-gray-700 text-lg leading-relaxed">
            A home loan is a secured loan offered by banks and financial institutions to help you purchase, construct, renovate, or extend a residential property. With long repayment tenures and competitive interest rates, owning your dream home becomes an achievable reality. Whether you're a first‑time buyer or looking to upgrade,  CashPro Financial Services simplifies the process by comparing offers from top lenders and guiding you through every step.
          </p>
        </div>
        <div className="mb-16 bg-gradient-to-r from-green-50 to-green-50 rounded-xl p-8 animate-fade-up animation-delay-200">
          <p className="text-lg font-semibold text-gray-800 mb-4">Use our home loan EMI calculator to calculate the loan EMI . The online loan calculator gives results based on principal amount, interest rate and tenure. The loan calculator also displays total interest payable, total principal payable and amortization schedule to help you understand your home loan repayment better.</p>
          <div className="flex flex-col md:flex-row gap-8 items-center mt-6">
            <div className="flex-1">
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Loan Amount: ₹{principal.toLocaleString()}</label>
                  <input type="range" min="500000" max="100000000" step="100000" value={principal} onChange={(e) => setPrincipal(Number(e.target.value))} className="w-full" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Interest Rate: {interest}% p.a.</label>
                  <input type="range" min="6" max="12" step="0.1" value={interest} onChange={(e) => setInterest(Number(e.target.value))} className="w-full" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Tenure: {tenure} years</label>
                  <input type="range" min="1" max="30" step="1" value={tenure} onChange={(e) => setTenure(Number(e.target.value))} className="w-full" />
                </div>
              </div>
            </div>
            <div className="flex-1 bg-white p-6 rounded-xl shadow-md text-center">
              <Calculator className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Estimated Monthly EMI</p>
              <p className="text-4xl font-bold text-green-700">₹{emi.toLocaleString()}</p>
              <div className="mt-4 text-sm text-gray-500">
                <p>Total Interest Payable: ₹{totalInterest.toLocaleString()}</p>
                <p>Total Payment: ₹{totalPayable.toLocaleString()}</p>
              </div>
              <button className="mt-4 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-full transition duration-300">Apply Now</button>
            </div>
          </div>
        </div>
        <div className="mb-16 text-center animate-fade-up animation-delay-400">
          <p className="text-lg font-semibold text-green-700 bg-green-50 inline-block px-6 py-3 rounded-full shadow-sm">
            Home loan interest rate - 8.85%*<br />
            We compare and offer best interest rate possible!
          </p>
        </div>
        <div className="mb-16 bg-white rounded-xl shadow-md p-8 animate-fade-up animation-delay-600">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Eligibility Criteria:</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "Nationality: Indian Residents, Non-Resident Indians (NRIs), and Persons of Indian Origin (PIOs) are eligible",
              "Credit Score: Preferably 750 and above for better approval chances and lower rates",
              "Age Limit: Minimum 18 years; maximum up to 70 years at loan maturity",
              "Work Experience: At least 2 years of stable employment for salaried applicants",
              "Business Continuity: Minimum 3 years of business existence for self-employed individuals",
              "Minimum Salary: At least ₹25,000 per month (varies by city and lender)",
              "Loan Amount: Up to 90% of the property value, subject to eligibility and property type"
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 animate-fade-up animation-delay-800">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Home Loan Types:</h2>
          <div className="space-y-6">
            {[
              { icon: Home, title: "Home Purchase Loan", desc: "Finance the purchase of ready-to-move-in properties (flats, row houses, villas), under-construction apartments, or pre-owned homes. The loan covers up to 90% of the property value." },
              { icon: Building, title: "Composite Loan", desc: "Ideal for buying a plot and constructing a house on it. Funds are disbursed in stages: first for the plot, then as construction progresses. This helps manage cash flow efficiently." },
              { icon: Home, title: "Home Construction Loan", desc: "If you already own a plot, this loan covers the cost of building your house. Disbursements are linked to construction milestones." },
              { icon: RefreshCw, title: "Home Renovation / Improvement Loan", desc: "Renovate, repair, or upgrade your existing home – from kitchen remodeling to structural repairs. Interest rates are usually the same as regular home loans." },
              { icon: ArrowUpCircle, title: "Home Extension Loan", desc: "Add extra space by building additional floors or expanding your current home. Funds are released based on the extension plan and cost estimates." },
              { icon: Clock, title: "Bridge Loan", desc: "A short-term solution when you need to buy a new house before selling your existing one. The loan bridges the gap until your old property is sold." },
              { icon: TrendingUp, title: "Interest Saver Loan (Overdraft)", desc: "Link your home loan account to your savings/current account. Surplus funds parked in the linked account reduce the principal on which interest is calculated, lowering your overall interest burden." },
              { icon: Zap, title: "Step Up Loan", desc: "Start with lower EMIs in the initial years and gradually increase them as your income grows. Perfect for young professionals expecting career progression." }
            ].map((type, idx) => (
              <div key={idx} className="flex items-start gap-4 pb-4 border-b border-gray-100 last:border-0">
                <type.icon className="w-8 h-8 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-1">{type.title}</h3>
                  <p className="text-gray-600">{type.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 bg-white rounded-xl shadow-md p-8 animate-fade-up animation-delay-1000">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Documents Required</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "Proof of Residence: Aadhaar Card, Passport, Voter's ID, Utility Bills (Electricity, Water, Gas – not older than 3 months), Bank Passbook, or Ration Card",
              "Proof of Identity: PAN Card, Passport, Aadhaar Card, Voter's ID, or Driving License",
              "Proof of Age: Aadhaar Card, PAN Card, Passport, Birth Certificate, 10th Marksheet, or Driving License",
              "Proof of Income (Salaried): Form 16, last 3 months' salary slips, IT Returns of past 3 years, and bank statements showing salary credit",
              "Proof of Income (Self-Employed): IT Returns of last 3 years, Balance Sheet & Profit & Loss Account (audited), Business License, and proof of business address",
              "Property Documents: NOC from society/builder, detailed construction cost estimate, registered sale deed, allotment letter, approved building plan, and property tax receipts"
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center animate-fade-up animation-delay-1200">
          <h2 className="text-3xl font-bold text-green-700 mb-4">We are Here to Help You</h2>
          <p className="text-gray-600 mb-6"> CashPro Financial Services is dedicated to making your home loan journey transparent, quick, and hassle‑free. Our experts compare offers from multiple lenders to secure the best deal for you – at no cost.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">APPLY FOR LOAN</p>
              <p className="text-gray-800 font-medium mt-1">Looking for a Home Loan?</p>
              <button className="mt-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-full transition duration-300 shadow">
                Apply Now
              </button>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">CALL US AT</p>
              <a href="tel:+918887141052" className="text-lg font-bold text-green-600 hover:underline">
                +91 88871 41052
              </a>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">MAIL US AT</p>
              <a href="mailto:info@cashpro.co.in" className="text-lg font-bold text-green-600 hover:underline">
                info@cashpro.co.in
              </a>
            </div>
          </div>
          <Link to="/contact" className="inline-block text-green-600 hover:underline font-medium">
            Contact Us →
          </Link>
        </div>
      </div>
      <style>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-600 {
          animation-delay: 0.6s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-800 {
          animation-delay: 0.8s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-1200 {
          animation-delay: 1.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
};

export default HomeLoan;